import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  Outlet,
  useParams,
  useLocation,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Styles and Theme
import "./index.css";
import rsTheme from "./theme/theme";

// Components
import NavBar from "./components/common/NavBar";
import { NotificationStack } from "./components/ui";
import NotAuthorized from "./components/common/NotAuthorized";
import PageNotFound from "./components/common/PageNotFound";
import RequireAuth from "./components/common/RequireAuth";
import VersionUpdateBanner from "./components/common/VersionUpdateBanner";
import { MessageModal } from "./components/ui";

// Pages
import {
  DailyCost,
  DailyDispatch,
  Schedule,
  DevShoppingMall,
  TicketDemo,
  Drive,
  ErrorTest,
  Home,
  HoursLog,
  WorkerClock,
  UploadMedia,
  WorkerSchedule,
  WorkerTimesheet,
  PayoutTimesheet,
  PayoutTimesheetDashboard,
  TMLog,
  TMReport,
  TMPrice,
  TMInspector,
  RFILog,
  RFIDetail,
  SafetyMeeting,
  SMSTerms,
  ProjectPage,
  ProjectsHome,
} from "./pages";
import Login from "./pages/Login";

// Hooks and Context
import {
  PlanningProvider,
  usePlanningContext,
  ScheduleProvider,
} from "./context/domain/planning";
import useCoreCtx, { CoreContext } from "./context/global/CoreContext";
import { VersionUpdateProvider, useVersionUpdate } from "./context/global/VersionUpdateContext";
import useTimekeepingCtx, {
  TimekeepingContext,
} from "./context/domain/timekeeping/TimekeepingContext";
import { FilesProvider } from "./context/domain/files/FilesContext";
import useTMLogCtx, {
  TMLogContext,
} from "./context/domain/operations/TMLogContext";
import useRFICtx, {
  RFIContext,
} from "./context/domain/operations/RFIContext";
import LanguageForceRedirect from "./components/common/LanguageForceRedirect";
import { FEATURE_FLAGS, LANGUAGE_CONFIG, VERSION_CHECK_CONFIG } from "./config";
import { isLanguageForcePath } from "./utils/auth";

/**
 * DO NOT USE for new links. Legacy `/dispatch/:date` redirect to `/planning/dispatch/:date`.
 *
 * @returns {JSX.Element} Navigate to `/planning/dispatch/:date`.
 */
const DispatchRedirect = () => {
  const { date } = useParams();
  return (
    <Navigate
      to={date ? `/planning/dispatch/${date}` : "/planning/dispatch"}
      replace
    />
  );
};

/**
 * DO NOT USE for new links. Legacy `/clock-in/:date` redirect to `/timekeeping/clock-in/:date`.
 *
 * @returns {JSX.Element} Navigate to `/timekeeping/clock-in/:date`.
 */
const ClockInRedirect = () => {
  const { date } = useParams();
  return (
    <Navigate
      to={date ? `/timekeeping/clock-in/${date}` : "/timekeeping/clock-in"}
      replace
    />
  );
};

/**
 * DO NOT USE for new links. Legacy `/hours-log/:date` redirect to `/timekeeping/hours-log/:date`.
 *
 * @returns {JSX.Element} Navigate to `/timekeeping/hours-log/:date`.
 */
const HoursLogRedirect = () => {
  const { date } = useParams();
  return (
    <Navigate
      to={date ? `/timekeeping/hours-log/${date}` : "/timekeeping/hours-log"}
      replace
    />
  );
};

/**
 * Provides weekly TimekeepingContext for clock-in, schedule, hours-log, and worker timesheet routes.
 *
 * @returns {JSX.Element} Outlet wrapped in TimekeepingContext.
 */
const TimekeepingShiftLayout = () => {
  const timekeepingCtx = useTimekeepingCtx();

  return (
    <TimekeepingContext.Provider value={timekeepingCtx}>
      <Outlet />
    </TimekeepingContext.Provider>
  );
};

/**
 * Timekeeping route tree, including payout review (no weekly shift context) and shift pages.
 *
 * @returns {JSX.Element} Timekeeping Routes.
 */
const TimekeepingRoutes = () => {
  return (
    <Routes>
      {/* Payout review does not use weekly shift context; keep it off that provider. */}
      <Route
        path="/payout-timesheets"
        element={
          <RequireAuth>
            <PayoutTimesheetDashboard />
          </RequireAuth>
        }
      />
      <Route
        path="/payout-timesheet/:userId/:weekEnding"
        element={
          <RequireAuth>
            <PayoutTimesheet />
          </RequireAuth>
        }
      />
      <Route
        path="/timesheets"
        element={<Navigate to="/timekeeping/payout-timesheets" replace />}
      />
      <Route element={<TimekeepingShiftLayout />}>
        <Route
          path="/clock-in/:date?"
          element={
            <RequireAuth>
              <WorkerClock />
            </RequireAuth>
          }
        />
        <Route
          path="/worker-schedule/:date/user/:userId"
          element={
            <RequireAuth>
              <WorkerSchedule />
            </RequireAuth>
          }
        />
        <Route
          path="/worker-schedule/:date"
          element={
            <RequireAuth>
              <WorkerSchedule />
            </RequireAuth>
          }
        />
        <Route
          path="/worker-schedule"
          element={
            <RequireAuth>
              <WorkerSchedule />
            </RequireAuth>
          }
        />
        <Route
          path="/hours-log/:date?"
          element={
            <RequireAuth>
              <HoursLog />
            </RequireAuth>
          }
        />
        <Route
          path="/worker-timesheet/:date?"
          element={
            <RequireAuth>
              <WorkerTimesheet />
            </RequireAuth>
          }
        />
      </Route>
    </Routes>
  );
};

/**
 * Files domain routes wrapped in FilesProvider (upload and shared drive).
 *
 * @returns {JSX.Element} Files Routes.
 */
const FilesRoutes = () => {
  return (
    <FilesProvider>
      <Routes>
        {/* Files & Files */}
        <Route
          path="/upload"
          element={
            <RequireAuth>
              <UploadMedia />
            </RequireAuth>
          }
        />
        <Route
          path="/drive"
          element={
            <RequireAuth>
              <Drive />
            </RequireAuth>
          }
        />
      </Routes>
    </FilesProvider>
  );
};

/**
 * Hosts the planning toast stack so dispatch and schedule share one notification queue.
 *
 * @returns {JSX.Element} Planning NotificationStack.
 */
const PlanningNotificationHost = () => {
  const { notificationItems, dismissNotification } = usePlanningContext();
  return (
    <NotificationStack
      items={notificationItems ?? []}
      onDismiss={dismissNotification}
    />
  );
};

/**
 * Planning domain routes: daily dispatch and the schedule timeline (with ScheduleProvider).
 *
 * @returns {JSX.Element} Planning Routes.
 */
const PlanningRoutes = () => {
  return (
    <PlanningProvider>
      <PlanningNotificationHost />
      <Routes>
        {/* Planning apps */}
        <Route
          path="/dispatch/:date?"
          element={
            <RequireAuth>
              <DailyDispatch />
            </RequireAuth>
          }
        />
        <Route
          path="/schedule"
          element={
            <RequireAuth>
              <ScheduleProvider>
                <Schedule />
              </ScheduleProvider>
            </RequireAuth>
          }
        />
      </Routes>
    </PlanningProvider>
  );
};

/**
 * Analytics domain routes (daily cost).
 *
 * @returns {JSX.Element} Analytics Routes.
 */
const AnalyticsRoutes = () => {
  return (
    <Routes>
      {/* Analytics apps */}
      <Route
        path="/cost"
        element={
          <RequireAuth>
            <DailyCost />
          </RequireAuth>
        }
      />
    </Routes>
  );
};

/**
 * Projects module routes. Restricted to manager or executive.
 *
 * @returns {JSX.Element} Projects Routes.
 */
const ProjectsRoutes = () => {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <RequireAuth manager executive>
            <ProjectsHome />
          </RequireAuth>
        }
      />
      <Route
        path="/:projectId/*"
        element={
          <RequireAuth manager executive>
            <ProjectPage />
          </RequireAuth>
        }
      />
    </Routes>
  );
};

/**
 * Provides TMLogContext only around T&M pages.
 *
 * @param {Object} props Component props.
 * @param {React.ReactNode} props.children T&M page tree.
 * @returns {JSX.Element} TMLogContext provider.
 */
const TMLogWrapper = ({ children }) => {
  const tmLogCtx = useTMLogCtx();
  return <TMLogContext.Provider value={tmLogCtx}>{children}</TMLogContext.Provider>;
};

/**
 * Provides RFIContext only around RFI pages.
 *
 * @param {Object} props Component props.
 * @param {React.ReactNode} props.children RFI page tree.
 * @returns {JSX.Element} RFIContext provider.
 */
const RFIWrapper = ({ children }) => {
  const rfiCtx = useRFICtx();
  return <RFIContext.Provider value={rfiCtx}>{children}</RFIContext.Provider>;
};

/**
 * Operations routes for T&M, RFI, and safety meetings.
 *
 * @returns {JSX.Element} Operations Routes.
 */
const OperationsRoutes = () => {
  return (
    <Routes>
      {/* T&M Log pages - wrapped with TMLogContext */}
      <Route
        path="/tm-log"
        element={
          <RequireAuth>
            <TMLogWrapper>
              <TMLog />
            </TMLogWrapper>
          </RequireAuth>
        }
      />
      <Route
        path="/tm-log/report/:id?"
        element={
          <RequireAuth>
            <TMLogWrapper>
              <TMReport />
            </TMLogWrapper>
          </RequireAuth>
        }
      />
      <Route
        path="/tm-log/price/:reportId"
        element={
          <RequireAuth>
            <TMLogWrapper>
              <TMPrice />
            </TMLogWrapper>
          </RequireAuth>
        }
      />
      {/* RFI Log pages - wrapped with RFIContext */}
      <Route
        path="/rfi-log"
        element={
          <RequireAuth>
            <RFIWrapper>
              <RFILog />
            </RFIWrapper>
          </RequireAuth>
        }
      />
      <Route
        path="/rfi/:id"
        element={
          <RequireAuth>
            <RFIWrapper>
              <RFIDetail />
            </RFIWrapper>
          </RequireAuth>
        }
      />
      {/* Safety Meetings page - no ticket context needed */}
      <Route
        path="/safetymeeting"
        element={
          <RequireAuth>
            <SafetyMeeting />
          </RequireAuth>
        }
      />
    </Routes>
  );
};

/**
 * Development-only demo and test routes.
 *
 * @returns {JSX.Element} Dev Routes.
 */
const DevRoutes = () => {
  return (
    <Routes>
      {/* Dev and example pages */}
      <Route
        path="/shopping"
        element={
          <RequireAuth>
            <DevShoppingMall />
          </RequireAuth>
        }
      />
      <Route
        path="/error-test"
        element={
          <RequireAuth>
            <ErrorTest />
          </RequireAuth>
        }
      />
      <Route
        path="/ticket-demo"
        element={
          <RequireAuth>
            <TicketDemo />
          </RequireAuth>
        }
      />
    </Routes>
  );
};

/**
 * Top-level authenticated route tree, including legacy URL redirects.
 * Legacy URL Redirects - DO NOT USE - redirect old URLs to new prefixed URLs.
 *
 * @returns {JSX.Element} Application Routes.
 */
const AppRoutes = () => (
  <Routes>
    {/* Home */}
    <Route
      path="/"
      element={
        <RequireAuth>
          <Home />
        </RequireAuth>
      }
    />
    <Route path="/home" element={<Navigate to="/" replace />} />

    {/* SMS Terms Page - Public route */}
    <Route path="/sms-terms" element={<SMSTerms />} />

    {/* Legacy URL Redirects - DO NOT USE - redirect old URLs to new prefixed URLs */}
    <Route
      path="/dispatch"
      element={<Navigate to="/planning/dispatch" replace />}
    />
    <Route path="/dispatch/:date" element={<DispatchRedirect />} />
    <Route path="/cost" element={<Navigate to="/analytics/cost" replace />} />
    <Route path="/drive" element={<Navigate to="/files/drive" replace />} />
    <Route path="/upload" element={<Navigate to="/files/upload" replace />} />
    <Route
      path="/clock-in"
      element={<Navigate to="/timekeeping/clock-in" replace />}
    />
    <Route path="/clock-in/:date" element={<ClockInRedirect />} />
    <Route
      path="/weekly-timesheet"
      element={<Navigate to="/timekeeping/worker-schedule" replace />}
    />
    <Route
      path="/hours-log"
      element={<Navigate to="/timekeeping/hours-log" replace />}
    />
    <Route path="/hours-log/:date" element={<HoursLogRedirect />} />
    <Route
      path="/worker-timesheet"
      element={<Navigate to="/timekeeping/worker-timesheet" replace />}
    />

    {/* Additional common redirects */}
    <Route
      path="/timekeeping"
      element={<Navigate to="/timekeeping/clock-in" replace />}
    />
    <Route
      path="/planning"
      element={<Navigate to="/planning/dispatch" replace />}
    />
    <Route path="/files" element={<Navigate to="/files/drive" replace />} />
    <Route
      path="/analytics"
      element={<Navigate to="/analytics/cost" replace />}
    />

    {/* New Prefixed Routes with Context */}
    <Route path="/timekeeping/*" element={<TimekeepingRoutes />} />
    <Route path="/files/*" element={<FilesRoutes />} />
    <Route path="/planning/*" element={<PlanningRoutes />} />
    <Route path="/analytics/*" element={<AnalyticsRoutes />} />
    <Route path="/projects/*" element={<ProjectsRoutes />} />
    <Route path="/operations/*" element={<OperationsRoutes />} />
    <Route path="/dev/*" element={<DevRoutes />} />

    {/* Catch-all for 404 etc */}
    <Route path="/not-authorized" element={<NotAuthorized />} />
    <Route path="*" element={<PageNotFound />} />
  </Routes>
);

/**
 * Authenticated shell: CoreContext, nav, version banner, and AppRoutes.
 *
 * @returns {JSX.Element} Authenticated application shell.
 */
const AuthenticatedApp = () => {
  const coreCtx = useCoreCtx();
  const { notifyNewVersion } = useVersionUpdate();
  const { user, impersonation, announcement, fetchAnnouncement, clearAnnouncement } =
    coreCtx;

  // Connect version check service callback to context
  useEffect(() => {
    if (FEATURE_FLAGS.ENABLE_VERSION_CHECK && window.__versionCheckService) {
      window.__versionCheckService.setOnNewVersionCallback(notifyNewVersion);
    }
  }, [notifyNewVersion]);

  // One shared timer: version check and announcement GET fire on the same tick.
  useEffect(() => {
    /**
     * Run both update checks on one tick so the banner and modal can appear together.
     *
     * @returns {void}
     */
    const tick = () => {
      if (window.__versionCheckService) {
        window.__versionCheckService.checkVersion();
      }
      if (user && !impersonation?.active) {
        fetchAnnouncement();
      }
    };

    tick();
    const intervalId = window.setInterval(
      tick,
      VERSION_CHECK_CONFIG.CHECK_INTERVAL
    );
    return () => window.clearInterval(intervalId);
  }, [user, impersonation?.active, fetchAnnouncement]);

  return (
    <CoreContext.Provider value={coreCtx}>
      <NavBar />
      <VersionUpdateBanner />
      <AppRoutes />
      <MessageModal
        isOpen={Boolean(announcement)}
        title={announcement?.title || ""}
        message={announcement?.body || ""}
        onClose={clearAnnouncement}
        html
        icon={announcement?.icon}
      />
    </CoreContext.Provider>
  );
};

/**
 * Router-inner root that skips CoreContext on public inspector, login, and
 * language-force (`/en`, `/es`) routes.
 *
 * @returns {JSX.Element} Public or authenticated app tree.
 */
const AppWithContext = () => {
  const { pathname } = useLocation();
  // Login, inspector, and /app/en|/app/es skip CoreContext (guests have no session).
  const isPublicRoute =
    pathname.startsWith("/inspector/") ||
    pathname.startsWith("/login") ||
    isLanguageForcePath(pathname);

  return (
    <VersionUpdateProvider>
      <div className="bg-custom-rsbg font-inter caret-transparent min-h-screen w-full min-w-0">
        <ThemeProvider theme={rsTheme}>
          <CssBaseline />
          {isPublicRoute ? (
            // Public routes render without CoreContext to avoid auth API calls
            <Routes>
              <Route path="/inspector/:token" element={<TMInspector />} />
              <Route path="/login" element={<Login />} />
              <Route path="/login/es" element={<Login />} />
              <Route path="/login/en" element={<Login />} />
              <Route
                path="/es"
                element={
                  <LanguageForceRedirect language={LANGUAGE_CONFIG.SPANISH} />
                }
              />
              <Route
                path="/en"
                element={
                  <LanguageForceRedirect language={LANGUAGE_CONFIG.ENGLISH} />
                }
              />
            </Routes>
          ) : (
            // Authenticated routes with CoreContext
            <AuthenticatedApp />
          )}
        </ThemeProvider>
      </div>
    </VersionUpdateProvider>
  );
};

/**
 * Application root: `/app` basename router wrapping AppWithContext.
 *
 * @returns {JSX.Element} React Router application.
 */
export default function App() {
  return (
    <Router basename="/app">
      <AppWithContext />
    </Router>
  );
}
